import { useTranslation } from "react-i18next";
import cn from "classnames";
import type { CashFlowResponse } from "@/features/accounting/financial-reports/types";
import { formatReportAmount, formatDateRange } from "@/features/accounting/financial-reports/utils";
import { toNumber } from "@/shared/utils";
import { Skeleton } from "@/shared/ui";

interface CashFlowCardProps {
  data?: CashFlowResponse;
  isLoading?: boolean;
  error?: unknown;
  className?: string;
}

export default function CashFlowCard({ data, isLoading, error, className }: CashFlowCardProps) {
  const { t } = useTranslation("FinancialReportPage");

  if (isLoading) {
    return (
      <div className={cn("py-5 px-6 radius-3xl surface-base-fill", className)}>
        <Skeleton width={300} height={24} className="mb-4" />
        <div className="space-y-4">
          <Skeleton width="100%" height={100} />
          <Skeleton width="100%" height={200} />
          <Skeleton width="100%" height={60} />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn("py-5 px-6 radius-3xl surface-base-fill", className)}>
        <h3 className="text-body-bold-lg content-base-primary mb-4">{t("cashFlow.title")}</h3>
        <div className="text-body-regular-md content-action-neutral">
          {error ? t("cashFlow.error") : t("cashFlow.noData")}
        </div>
      </div>
    );
  }

  const periodLabel = formatDateRange(data.start_date, data.end_date);
  const netChange = formatReportAmount(data.net_cash_change);

  interface FlowItem {
    label: string;
    value: number;
    key: string;
    subItems?: { label: string; value: number; key: string }[];
  }

  const inflowsItems: FlowItem[] = [
    { label: t("cashFlow.inflows.grant"), value: toNumber(data.inflows.grant_receipts), key: "grant" },
    { label: t("cashFlow.inflows.membership"), value: toNumber(data.inflows.membership_receipts), key: "membership" },
    { label: t("cashFlow.inflows.gso"), value: toNumber(data.inflows.gso_receipts), key: "gso" },
    { label: t("cashFlow.inflows.donation"), value: toNumber(data.inflows.donation_receipts), key: "donation" },
    { label: t("cashFlow.inflows.deposit"), value: toNumber(data.inflows.deposit_interest), key: "deposit" },
    { label: t("cashFlow.inflows.charity"), value: toNumber(data.inflows.charity_receipts ?? "0"), key: "charity" },
    { label: t("cashFlow.inflows.sponsorship"), value: toNumber(data.inflows.sponsorship_receipts ?? "0"), key: "sponsorship" },
    { label: t("cashFlow.inflows.entrance"), value: toNumber(data.inflows.entrance_fees ?? "0"), key: "entrance" },
    { label: t("cashFlow.inflows.rental"), value: toNumber(data.inflows.rental_income ?? "0"), key: "rental" },
    { label: t("cashFlow.inflows.other"), value: toNumber(data.inflows.other_receipts), key: "other" },
  ].filter((item) => item.value !== 0);

  const hasSalarySplit = data.outflows.staff_salary_payments != null || data.outflows.gph_payments != null;
  const salaryItem: FlowItem = {
    label: t("cashFlow.outflows.salary"),
    value: toNumber(data.outflows.salary_payments),
    key: "salary",
    subItems: hasSalarySplit
      ? [
          { label: t("cashFlow.outflows.staffSalary"), value: toNumber(data.outflows.staff_salary_payments ?? "0"), key: "staffSalary" },
          { label: t("cashFlow.outflows.gph"), value: toNumber(data.outflows.gph_payments ?? "0"), key: "gph" },
        ].filter((s) => s.value !== 0)
      : undefined,
  };

  const outflowsItems: FlowItem[] = [
    salaryItem,
    { label: t("cashFlow.outflows.tax"), value: toNumber(data.outflows.tax_payments), key: "tax" },
    { label: t("cashFlow.outflows.pension"), value: toNumber(data.outflows.pension_payments), key: "pension" },
    { label: t("cashFlow.outflows.social"), value: toNumber(data.outflows.social_payments), key: "social" },
    { label: t("cashFlow.outflows.vendor"), value: toNumber(data.outflows.vendor_payments), key: "vendor" },
  ].filter((item) => item.value !== 0);

  const closingCash = formatReportAmount(data.closing_cash);

  return (
    <div className={cn("py-5 px-6 radius-3xl surface-base-fill flex flex-col gap-5", className)}>
      <h3 className="text-body-bold-lg content-base-primary">{t("cashFlow.title")}</h3>

      {inflowsItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center gap-5">
            <p className="text-label-md content-base-primary">{t("cashFlow.inflowsSection")}</p>
            <p className="text-label-sm content-base-low">{periodLabel}</p>
          </div>

          <div className="flex flex-col">
            {inflowsItems.map((item) => (
              <div key={item.key} className="py-3 border-t surface-container-stroke">
                <div className="flex justify-between items-center gap-5">
                  <p className="text-body-regular-md content-action-neutral">{item.label}</p>
                  <p className="text-body-bold-lg content-base-primary text-right">{formatReportAmount(item.value).text}</p>
                </div>
              </div>
            ))}
            <div className="py-3 border-t surface-container-stroke">
              <div className="flex justify-between items-center gap-5">
                <p className="text-body-bold-md content-action-neutral">{t("cashFlow.totalInflows")}</p>
                <p className="text-body-bold-lg content-base-primary text-right">{formatReportAmount(data.inflows.total).text}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center gap-5">
          <p className="text-label-md content-base-primary">{t("cashFlow.outflowsSection")}</p>
          <p className="text-label-sm content-base-low">{periodLabel}</p>
        </div>

        <div className="flex flex-col">
          {outflowsItems.map((item) => (
            <div key={item.key}>
              <div className="py-3 border-t surface-container-stroke">
                <div className="flex justify-between items-center gap-5">
                  <p className="text-body-regular-md content-action-neutral">{item.label}</p>
                  <p className="text-body-bold-lg content-base-primary text-right">{formatReportAmount(-item.value).text}</p>
                </div>
              </div>
              {item.subItems?.map((sub) => (
                <div key={sub.key} className="py-2 border-t surface-container-stroke pl-12">
                  <div className="flex justify-between items-center gap-5">
                    <p className="text-body-regular-sm content-base-secondary">{sub.label}</p>
                    <p className="text-body-regular-sm content-base-secondary text-right">{formatReportAmount(-sub.value).text}</p>
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div className="py-3 border-t surface-container-stroke">
            <div className="flex justify-between items-center gap-5">
              <p className="text-body-bold-md content-action-neutral">{t("cashFlow.totalOutflows")}</p>
              <p className="text-body-bold-lg content-base-primary text-right">
                {formatReportAmount(-toNumber(data.outflows.total)).text}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-3 p-4 radius-md surface-component-fill">
          <div className="flex justify-between items-center gap-5">
            <p className="text-body-regular-md content-base-primary">{t("cashFlow.openingBalance")}</p>
            <p className="text-body-bold-lg content-base-primary text-right">{formatReportAmount(data.opening_cash).text}</p>
          </div>
          <div className="flex justify-between items-center gap-5">
            <p className="text-body-regular-md content-base-primary">{t("cashFlow.netChange")}</p>
            <p className="text-body-bold-lg content-base-primary text-right">
              {netChange.text}
            </p>
          </div>
          <div className="flex justify-between items-center gap-5">
            <p className="text-body-regular-md content-base-primary">{t("cashFlow.endingBalance")}</p>
            <p className="text-heading-lg content-base-primary text-right">
              {closingCash.text}
            </p>
          </div>
        </div>

        <p className="text-body-regular-sm content-base-low text-center">{t("cashFlow.summaryFormula")}</p>
      </div>
    </div>
  );
}

